import { PropsWithChildren } from "react";
import { X } from "lucide-react";
import ButtonLink from "src/components/common/ButtonLink";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "src/components/shadcn";
import useIsMobile from "src/hooks/useIsMobile";

const MOBILE_APP_BAR_HEIGHT = 60;

const SIZES = {
  sm: "!w-[440px] !max-w-[440px]",
  md: "!w-[700px] !max-w-[700px]",
  lg: "!w-[800px] !max-w-[800px]",
};

export type SidebarBaseProps = PropsWithChildren & {
  isSidebarOpen: boolean;
  onSidebarToggleClick: (isOpen?: boolean) => void;
  size?: "sm" | "md" | "lg";
  offsetHeight?: number;
  title?: React.ReactNode;
};

const SidebarBase = ({
  children,
  isSidebarOpen,
  onSidebarToggleClick,
  size = "md",
  offsetHeight = 0,
  title = ""
}: SidebarBaseProps) => {
  const isMobile = useIsMobile();

  return (
    <Sheet
      open={isSidebarOpen}
      modal={false}
    >
      <SheetContent
        suppressOverlay
        suppressCloseButton
        style={isMobile ? {
          marginTop: MOBILE_APP_BAR_HEIGHT,
          height: `calc(100vh - ${MOBILE_APP_BAR_HEIGHT}px)`,
          right: 0,
          bottom: 0,
        } : {
          marginTop: offsetHeight + 16, // Add 16px top offset
          height: `calc(100vh - ${offsetHeight + 32}px)`, // Account for top and bottom offsets
          right: '16px', // Offset from right edge to show scrollbar
          bottom: '16px', // Add 16px bottom offset
        }}
        className={`gap-0 border border-gray-300 bg-white flex flex-col max-w-md shadow-lg pt-0 ${isMobile ? '!w-full !max-w-full rounded-none' : `rounded-lg ${SIZES[size]}`}`}
      >
        <SheetHeader className={`flex flex-row justify-between items-center text-gray-primary ${title ? 'py-2' : 'py-2'} min-h-[44px]`}>
          <SheetTitle>{title}</SheetTitle>
          <SheetClose className="!mt-0 p-1" onClick={() => onSidebarToggleClick(false)}>
            <X size={24} />
          </SheetClose>
        </SheetHeader>
      {children}
      <SheetFooter className="!justify-center mt-3">
        <ButtonLink className="text-xs font-semibold !text-gray-600" onClick={() => onSidebarToggleClick(false)}>
          Close
        </ButtonLink>
      </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default SidebarBase; 